import { DEFAULT_SHORTCUTS, type ShortcutAction, type ShortcutBinding } from '@/editor/KeyboardShortcuts';

/**
 * What the app says about itself when asked — the content behind the toolbar's ⓘ, with no DOM in it.
 *
 * ## Why an Info button exists at all
 *
 * Everything else in the HUD answers a question about the *garden*. Nothing answered questions about
 * the program: which build is this, which catalog is it reading, where is my work actually kept, and
 * what does the keyboard do. Those are asked rarely and urgently — usually in the same minute
 * somebody decides to report a problem, which is why the button sits beside the one that reports it.
 *
 * ## Nothing here is written down twice
 *
 * The version comes from the build (`src/version.ts`), the catalog figures from the loaded catalog,
 * and the keyboard reference from {@link DEFAULT_SHORTCUTS} — the same table the editor dispatches
 * on. A hand-maintained shortcut list is wrong within two milestones and is then worse than none,
 * because it teaches keys that do nothing.
 */

export interface AboutFacts {
  /** Build string, as stamped onto every feedback report. */
  appVersion: string;
  catalogVersion: string;
  /** How many plants that catalog holds. */
  catalogPlants: number;
  /** Where the open site is kept, in one phrase — "this browser", "your account". */
  storage: string;
  /** The open site's name, when one is open. */
  siteName?: string;
  /** Plants placed, and how many species they are drawn from. */
  sitePlants?: number;
  siteSpecies?: number;
  /** Where the site has been told it stands. Absent until it is localized. */
  place?: string;
  /** Suggestions and reports waiting in this browser — see `Feedback.ts`. */
  queued?: number;
}

export interface AboutRow {
  label: string;
  value: string;
}

/**
 * What Lush Glade is, in the two sentences someone reads before deciding whether their problem is
 * worth reporting. Kept to what is true of every deployment: the hosted tier's account story is the
 * account dialog's to tell, and the `Kept in` row already says which shape this one is running.
 */
export const ABOUT_BLURB =
  'Lush Glade is an interactive 3D landscape design environment: lay out real plants on real ' +
  'ground, watch them grow through the seasons, and photograph the result. Every figure it shows ' +
  'comes from a botanical catalog that ships with the build, read against the climate of the place ' +
  'you localize the site to.';

/**
 * The summary rows, in the order the questions get asked.
 *
 * Site before build deliberately: someone opening this while working wants to confirm what they are
 * looking at, and someone opening it to report a fault reads the whole card anyway. Rows with
 * nothing to say are omitted rather than filled with an em dash — an empty row is a fact the reader
 * has to rule out, and there is no such fact here.
 */
export function aboutRows(facts: AboutFacts): AboutRow[] {
  const rows: AboutRow[] = [];
  if (facts.siteName !== undefined && facts.siteName !== '') {
    rows.push({ label: 'Open site', value: facts.siteName });
  }
  if (facts.sitePlants !== undefined) {
    rows.push({ label: 'Planted', value: describePlanting(facts.sitePlants, facts.siteSpecies) });
  }
  if (facts.place !== undefined && facts.place !== '') {
    rows.push({ label: 'Localized to', value: facts.place });
  }
  rows.push({ label: 'Kept in', value: facts.storage });
  rows.push({
    label: 'Flora catalog',
    value: `${facts.catalogVersion} — ${countOf(facts.catalogPlants, 'plant')}`,
  });
  rows.push({ label: 'Build', value: facts.appVersion });
  return rows;
}

function describePlanting(plants: number, species: number | undefined): string {
  if (plants === 0) return 'Nothing yet';
  const count = countOf(plants, 'plant');
  return species === undefined ? count : `${count} in ${countOf(species, 'species', 'species')}`;
}

function countOf(n: number, singular: string, plural = `${singular}s`): string {
  return `${String(n)} ${n === 1 ? singular : plural}`;
}

/**
 * How the local queue describes itself.
 *
 * Shown whether or not anything is in it, because the interesting case is the full one: a queue with
 * six reports in it means six things somebody meant to send and nobody has received, and the About
 * dialog is the only place that can say so.
 */
export function describeQueue(count: number): string {
  if (count === 0) return 'Nothing is waiting to be sent.';
  return (
    `${countOf(count, 'report')} raised here ${count === 1 ? 'is' : 'are'} held in this browser. ` +
    'Copy them out and send them on — nothing has been delivered.'
  );
}

/* ------------------------------- Keyboard reference ------------------------------- */

export interface KeyRow {
  /** Caps in press order, modifiers first. */
  readonly keys: readonly string[];
  readonly description: string;
}

export interface KeySection {
  readonly title: string;
  readonly rows: readonly KeyRow[];
}

/**
 * Which heading each command is read under.
 *
 * Total over {@link ShortcutAction} on purpose: adding a binding without deciding where it belongs
 * is then a type error rather than a command that quietly never appears in the reference.
 */
const SECTION_OF: Record<ShortcutAction, string> = {
  place: 'Tools',
  'plant-mode': 'Tools',
  'prev-plant': 'Tools',
  'next-plant': 'Tools',
  move: 'Tools',
  rotate: 'Tools',
  maturity: 'Tools',
  'paint-ground': 'Tools',
  'sculpt-ground': 'Tools',
  wand: 'Tools',
  tag: 'Tools',
  delete: 'Editing',
  cancel: 'Editing',
  undo: 'Editing',
  redo: 'Editing',
  'select-all': 'Editing',
  duplicate: 'Editing',
  copy: 'Editing',
  paste: 'Editing',
  link: 'Editing',
  unlink: 'Editing',
  group: 'Editing',
  ungroup: 'Editing',
  'focus-selection': 'Looking',
  'view-perspective': 'Looking',
  'view-top': 'Looking',
  'view-front': 'Looking',
  'toggle-grid': 'Looking',
  'toggle-topo': 'Looking',
  'toggle-pointers': 'Looking',
  'photo-mode': 'Looking',
  save: 'Project',
  'save-as': 'Project',
  rename: 'Project',
  'toggle-history': 'Project',
};

/** Reading order for the headings, which is not the order the matcher wants them in. */
const SECTION_ORDER = ['Tools', 'Editing', 'Looking', 'Project'] as const;

/** Keys whose event name is not what is printed on them. */
const KEY_CAPS: Record<string, string> = {
  f2: 'F2',
  delete: 'Del',
  backspace: 'Backspace',
  escape: 'Esc',
};

/** One binding as printed caps — `Ctrl`, `Shift`, then the key. */
export function shortcutKeys(binding: ShortcutBinding): string[] {
  const keys: string[] = [];
  if (binding.ctrl) keys.push('Ctrl');
  if (binding.shift) keys.push('Shift');
  keys.push(KEY_CAPS[binding.key] ?? (binding.key.length === 1 ? binding.key.toUpperCase() : binding.key));
  return keys;
}

/**
 * The shortcut map as a printable reference.
 *
 * One row per command, first binding winning: the map lists `Delete` and `Backspace` for one action
 * and two ways to redo, which is right for matching and wrong for reading — a reference that says
 * "Redo" twice reads as a mistake in the reference rather than as generosity in the bindings.
 * Empty sections are dropped, so a heading never appears over nothing.
 */
export function keyReference(bindings: readonly ShortcutBinding[] = DEFAULT_SHORTCUTS): KeySection[] {
  const seen = new Set<ShortcutAction>();
  const byTitle = new Map<string, KeyRow[]>();

  for (const binding of bindings) {
    if (seen.has(binding.action)) continue;
    seen.add(binding.action);
    const title = SECTION_OF[binding.action];
    const rows = byTitle.get(title) ?? [];
    rows.push({ keys: shortcutKeys(binding), description: binding.description });
    byTitle.set(title, rows);
  }

  return SECTION_ORDER.flatMap((title) => {
    const rows = byTitle.get(title);
    return rows && rows.length > 0 ? [{ title, rows }] : [];
  });
}
